// src/hooks/useSchedule.js
import { useState, useEffect, useCallback } from 'react'
import { toDateStr } from '../lib/attendance.js'
import { getAgents, addAgent, updateAgent, deleteAgent, getOffices, addOffice, updateOffice, deleteOffice, setPhoneAgentByNumber, renameOpenPhoneHistory, createSentavusUser, updateSentavusUser, getAppUsers, lockAgentLogin, getMyRole, supabase } from '../db/index.js'
import { syncAgentToCore } from '../db/core.js'
import { seedIfEmpty } from '../db/seed.js'
import { logEvent, diffLog, getCurrentAdmin } from '../lib/agentLog.js'
import { t, tIn, tLog } from '../lib/i18n.js'
import { DAYS } from '../lib/schedule.js'

// ─── Login-sync failures must reach a human (review 2026-09-23) ──────────────────
// lock-agent / create-agent / update-agent used to fail into console.error only, so a
// refused call (agent_hr 401'd on all three) closed the dialog as if the login had been
// locked. These report through a window event the Agents tab turns into its warn toast —
// the one surface mounted whenever these run from a dialog.
export const AGENT_SYNC_FAILED_EVENT = 'sentavus:agent-sync-failed'
export function reportAgentSyncFailure(agentName, err) {
  const detail = err?.message || String(err || '')
  console.error('[sentavus] agent login sync failed:', agentName, detail)
  try {
    window.dispatchEvent(new CustomEvent(AGENT_SYNC_FAILED_EVENT, {
      detail: { msg: `${t('common_action_failed')} ${agentName || ''}: ${detail}`.trim() },
    }))
  } catch { /* non-browser (tests) — the console line above still stands */ }
}
// Lock/unlock with the failure REPORTED. A 200 carrying core_error is a failure too:
// core is the login gate, so a scheduler-only ban does not stop a sign-in.
export async function lockLoginReported(loginId, locked, agentName) {
  try {
    const out = await lockAgentLogin(loginId, locked)
    if (out?.core_error) throw new Error(out.core_error)
    return true
  } catch (e) { reportAgentSyncFailure(agentName, e); return false }
}

// Only management runs the auto-resign pass (review 2026-09-23): lock-agent refuses
// plain agents, and a pass run in their client flipped is_active and then failed the
// lock — after which the is_active filter never retried it. == server SCHED_MGMT_ROLES.
const AUTO_RESIGN_ROLES = new Set(['admin', 'manager', 'general_manager'])
const AUTO_LOCK_RETRY_DAYS = 30
const LS_AUTO_LOCK_DONE = 'sntv_auto_resign_lock_done_v1' // { [agentId]: resign_date }
function readAutoLockDone() {
  try { return JSON.parse(localStorage.getItem(LS_AUTO_LOCK_DONE) || '{}') || {} } catch { return {} }
}
function markAutoLockDone(agent) {
  try {
    const m = readAutoLockDone(); m[agent.id] = agent.resign_date
    localStorage.setItem(LS_AUTO_LOCK_DONE, JSON.stringify(m))
  } catch { /* storage unavailable — worst case is one more idempotent lock call */ }
}
// Retry the lock for RECENT AUTO-resignations only. The roster holds no lock flag, so
// "was the auto-lock ever applied?" is unknowable here; lock-agent is idempotent (a
// GoTrue ban PUT), so re-applying is safe. Restricted to agents whose LATEST 'left' log
// is the auto-resign line, so an admin who deliberately unticked "lock login" in the
// Mark-as-left dialog is not overridden. Each machine stops retrying once one succeeds.
async function retryAutoResignLocks(roster, today) {
  if (!supabase) return
  const since = toDateStr(new Date(Date.now() - AUTO_LOCK_RETRY_DAYS * 86400000))
  const done = readAutoLockDone()
  const cands = roster.filter(x => x.is_active === false && x.login_id && x.resign_date &&
    x.resign_date <= today && x.resign_date >= since && done[x.id] !== x.resign_date)
  if (!cands.length) return
  const { data, error } = await supabase.from('agent_logs')
    .select('agent_id,message,ts').eq('category', 'left')
    .in('agent_id', cands.map(x => x.id)).order('ts', { ascending: false })
  if (error) { console.warn('[sentavus] auto-resign lock retry: log read failed:', error.message); return }
  const autoMsgs = new Set(['en', 'ka'].map(l => tIn(l, 'agents_auto_resigned_log')))
  const latest = {}
  for (const r of data || []) if (!(r.agent_id in latest)) latest[r.agent_id] = r
  for (const x of cands) {
    if (!autoMsgs.has(latest[x.id]?.message)) continue
    if (await lockLoginReported(x.login_id, true, x.name)) markAutoLockDone(x)
  }
}

// ─── Granular schedule diff ───────────────────────────────────────────────────
// Compares old and new schedule objects and returns only the lines that changed.
function diffSchedule(prev, next) {
  const changes = []
  DAYS.forEach(d => {
    const oldSlots = (Array.isArray(prev?.[d]) ? prev[d] : []).filter(s => s.start && s.end)
    const newSlots = (Array.isArray(next?.[d]) ? next[d] : []).filter(s => s.start && s.end)

    const oldStr = oldSlots.length === 0 ? 'off'
      : oldSlots.map(s => `${s.start}–${s.end} ${s.location}`).join(' + ')
    const newStr = newSlots.length === 0 ? 'off'
      : newSlots.map(s => `${s.start}–${s.end} ${s.location}`).join(' + ')

    if (oldStr !== newStr) {
      changes.push(`${d}: ${oldStr} → ${newStr}`)
    }
  })
  return changes
}

export function useSchedule() {
  const [agents,  setAgents]  = useState([])
  const [offices, setOffices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  const reload = useCallback(async () => {
    try {
      setLoading(true)
      await seedIfEmpty()
      const [a, o] = await Promise.all([getAgents(), getOffices()])

      // ⚠️ PUBLISH THE ROSTER FIRST, THEN run the side effects (2026-08-01).
      // Everything below this line is a WRITE loop, and it used to sit BETWEEN the fetch
      // and setAgents inside this same try — so one failing write left the entire app with
      // an EMPTY roster. That is not a local bug: `agents` is how every surface turns an id
      // into a person, so the Listings rail fell back to raw event emails, filed everyone
      // under "Off", dropped the shift bands, and meeting markers (keyed on agents.id)
      // landed on rows that no longer existed. A read must never depend on a write.
      setAgents(a)
      setOffices(o)
      setError(null)

      // Auto-resign agents whose scheduled resign date has passed. Its own try/catch: this
      // is bookkeeping that runs on every load, and failing it is worth a console line, not
      // a blank application.
      try {
        const today = toDateStr(new Date())
        // Review 2026-09-23: management clients only — see AUTO_RESIGN_ROLES.
        const myRole = await getMyRole().catch(() => null)
        const toResign = !AUTO_RESIGN_ROLES.has(myRole) ? [] : a.filter(agent =>
          agent.is_active !== false &&
          agent.resign_date &&
          agent.resign_date <= today
        )
        for (const agent of toResign) {
          await updateAgent(agent.id, { is_active: false })
          logEvent(agent.id, agent.name, 'left', tLog('agents_auto_resigned_log'))
          agent.is_active = false
          // Scheduled resign date arrived with nobody clicking through a confirmation
          // dialog — lock the login the same way Mark-as-left does, so this path can't
          // leave a resigned agent with standing access. A failure is reported and the
          // retry below picks it up on the next management load.
          if (agent.login_id && await lockLoginReported(agent.login_id, true, agent.name)) markAutoLockDone(agent)
        }
        // New array identity so the mutated is_active flags above actually re-render.
        if (toResign.length) setAgents([...a])
        if (AUTO_RESIGN_ROLES.has(myRole)) await retryAutoResignLocks(a, today)
      } catch (e) {
        console.error('[sentavus] auto-resign pass failed (roster still loaded):', e.message)
      }
    } catch (err) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { reload() }, [reload])

  // agents.email is often blank for managers/admins (their real address lives on the
  // SEPARATE app_users login table instead — see 2026-07-02 Tako split-identity fix).
  // When an agent is linked to a scheduler login (auth_user_id), prefer that login's
  // email for the core party so it isn't left null the way Tako's originally was.
  async function resolveCoreEmail(agent) {
    if (!agent.auth_user_id) return agent.email
    try {
      const users = await getAppUsers()
      return users.find(u => u.auth_user_id === agent.auth_user_id)?.email || agent.email
    } catch { return agent.email }
  }

  const createAgent = useCallback(async (data) => {
    const agent = await addAgent(data)
    setAgents(prev => [...prev, agent])
    logEvent(agent.id, agent.name, 'hired',
      tLog('log_agent_added', { by: getCurrentAdmin(), role: agent.role, hire_date: data.hire_date ? tLog('log_diff_hire_date', { date: data.hire_date }) : '' })
    )
    if (data.phone_work) await setPhoneAgentByNumber(data.phone_work, agent.id, agent.name)
    // Mirror the agent into the property (sentavus) DB so they can log into the
    // extension and own cards. Non-blocking: the scheduler agent already exists.
    if (data.login_id && data.login_pass) {
      try {
        const out = await createSentavusUser(data.login_id, data.login_pass)
        // The same pair now also provisions the SCHEDULER login (server-side, 2026-07-05).
        // Auto-link the returned auth uid so the manual "Scheduler login" picker step
        // isn't needed for regular agents.
        // ⚠️ If the login already EXISTED, a brand-new agent must NOT adopt it — that account
        // belongs to someone else (this is how browser-autofill'd credentials silently took
        // over Tako's login, 2026-07-08). Same for a uid already owned by another agent.
        const clash = out?.sched_user_id && agents.find(a => a.auth_user_id === out.sched_user_id)
        if (out?.existed || clash) {
          console.error(`[sentavus] login "${data.login_id}" already exists — NOT linking it to new agent ${agent.id}; choose a unique login id`)
          reportAgentSyncFailure(agent.name, new Error(`login "${data.login_id}" already exists`))  // review 2026-09-23
        } else if (out?.sched_user_id && out.sched_user_id !== agent.auth_user_id) {
          const linked = await updateAgent(agent.id, { auth_user_id: out.sched_user_id })
          setAgents(list => list.map(a => a.id === agent.id ? linked : a))
        }
      }
      catch (e) { reportAgentSyncFailure(agent.name, e) }  // review 2026-09-23: was console-only
    }
    // Mirror into core as a party (migration 0040) so this agent's reshares/leads have
    // an identity to hang off. Non-blocking, same pattern as the property-DB mirror above.
    try { await syncAgentToCore({ agentId: agent.id, name: agent.name, email: await resolveCoreEmail(agent), phone: agent.phone_work }) }
    catch (e) { console.error('[core] agent sync failed:', e.message) }
    return agent
  }, [agents])  // agents: read by the "login already owned by another agent" clash guard above

  const editAgent = useCallback(async (id, updates) => {
    const prev  = agents.find(a => a.id === id)
    const agent = await updateAgent(id, updates)
    setAgents(list => list.map(a => a.id === id ? agent : a))
    const by = getCurrentAdmin()

    // Sync phone_numbers.agent_id; log history only when the work phone changes
    if (updates.phone_work !== undefined) {
      if (prev?.phone_work && prev.phone_work !== updates.phone_work) await setPhoneAgentByNumber(prev.phone_work, null, null)
      if (updates.phone_work) await setPhoneAgentByNumber(updates.phone_work, id, agent.name, updates.phone_work !== prev?.phone_work)
    }
    // phone_history.agent_name is a snapshot taken at assignment time, so a rename
    // otherwise leaves the still-active history row showing the old name forever.
    if (updates.name !== undefined && prev?.name !== agent.name) await renameOpenPhoneHistory(id, agent.name)

    // Propagate a login/password change to the property (sentavus) account. Non-blocking.
    const loginChanged = updates.login_id   !== undefined && (updates.login_id   || '') !== (prev?.login_id   || '')
    const passChanged  = updates.login_pass !== undefined && (updates.login_pass || '') !== (prev?.login_pass || '')
    const newLogin = updates.login_id ?? prev?.login_id
    if ((loginChanged || passChanged) && newLogin) {
      // ⚠️ Target the CURRENTLY chosen scheduler link, NEVER prev.auth_user_id. Sending the
      // stale prev link made the server rename whatever account was previously linked to the
      // new login_id — hijacking an unrelated agent's login even when the "Scheduler login"
      // dropdown was set to none (Tako↔Mariam incident, 2026-07-08). When the dropdown is
      // none we send '' so the server resolves by the agent's OWN email instead.
      const chosenLink = ('auth_user_id' in updates) ? (updates.auth_user_id || '') : (prev?.auth_user_id || '')
      // 2026-10-10: when the OLD login id is also carried by another agent, changing it is not
      // a rename — that account is the other agent's, and the server rightly refuses to move it
      // (Barbare Kevekhishvili shared `barbarek` with ბარბარე კოკილაშვილი; switching her to
      // `bakev` was refused and nothing was created). Leave the old account alone and create
      // a fresh one under the new id, which needs the password even if it was not retyped.
      const oldShared = loginChanged && !!prev?.login_id && agents.some(a =>
        a.id !== id && (a.login_id || '').toLowerCase().trim() === prev.login_id.toLowerCase().trim())
      const password = passChanged || oldShared ? (updates.login_pass ?? prev?.login_pass ?? '') : ''
      try {
        const out = await updateSentavusUser({
          oldLoginId: oldShared ? '' : (prev?.login_id || ''),
          loginId:    newLogin,
          password,
          schedAuthUserId: oldShared ? '' : chosenLink,
        })
        // Auto-link the scheduler auth uid (same pair provisions both logins since 2026-07-05),
        // but NEVER onto a uid that already belongs to a DIFFERENT agent — that is the hijack
        // above. Refuse loudly and leave the row's link untouched.
        if (out?.sched_user_id && out.sched_user_id !== agent.auth_user_id) {
          const clash = agents.find(a => a.id !== id && a.auth_user_id === out.sched_user_id)
          if (clash) {
            console.error(`[sentavus] refusing to link ${out.sched_user_id} to agent ${id}: already owned by ${clash.name} (${clash.id})`)
            reportAgentSyncFailure(agent.name, new Error(`login already belongs to ${clash.name}`))  // review 2026-09-23
          } else {
            const linked = await updateAgent(id, { auth_user_id: out.sched_user_id })
            setAgents(list => list.map(a => a.id === id ? linked : a))
          }
        }
      } catch (e) { reportAgentSyncFailure(agent.name, e) }  // review 2026-09-23: was console-only
    }

    // Re-sync core (migration 0040) only when identity fields actually changed — avoid
    // firing on every unrelated edit (schedule, attendance, etc). Non-blocking.
    if (updates.name !== undefined || updates.email !== undefined || updates.phone_work !== undefined || updates.auth_user_id !== undefined) {
      try { await syncAgentToCore({ agentId: id, name: agent.name, email: await resolveCoreEmail(agent), phone: agent.phone_work }) }
      catch (e) { console.error('[core] agent sync failed:', e.message) }
    }

    // Profile fields — only log what actually changed
    const profileFields = [
      { key: 'name',           label: tLog('log_field_name')           },
      { key: 'role',           label: tLog('log_field_role')           },
      { key: 'email',          label: tLog('log_field_email')          },
      { key: 'phone_work',     label: tLog('log_field_phone_work')     },
      { key: 'phone_personal', label: tLog('log_field_phone_personal') },
      { key: 'hire_date',      label: tLog('log_field_hire_date')      },
      // ── Rate changes are the only profile edits that move MONEY, and until 2026-09-20 they
      // were the only ones that left no trace at all: zero of ~10,000 agent_logs rows
      // mentioned a commission change, because these two keys were never in this list.
      // scheduler 0025 dates the spell in `agent_rate` (the trigger cannot be forgotten);
      // this line is the human half — WHO changed the percentage, which a trigger cannot know.
      { key: 'commission',         label: tLog('log_field_commission')         },
      { key: 'commission_manager', label: tLog('log_field_commission_manager') },
    ]
    const diff = diffLog(prev, updates, profileFields)
    if (diff) logEvent(id, agent.name, 'profile', tLog('log_agent_profile', { by, diff }))

    // ── B361 · a transfer is a lifecycle act, so it must leave a trace with a NAME on it ──
    // `agent_office_membership` (scheduler migration 0001) already dates the interval, and
    // its trigger cannot be forgotten — but a trigger fires inside the database and has no
    // idea WHO pressed save. "Who moved whom, when" is the whole point of an audit trail,
    // and the roster's log already carries an `office` category that was only ever used for
    // office SETTINGS. A transfer — the one office act about a person — wrote nothing.
    //
    // ⚠️ Deliberately NOT added to `profileFields` above, though it looks like it belongs
    // there. `diffLog` renders raw values, and an office_id is a uuid: "Office: '—' →
    // 'a23be823-2ff9-…'" is a line no human can read, and the log is read by people. Names
    // are resolved here, falling back to the id for an office deleted since the move —
    // an unreadable line still beats a missing one.
    if ('office_id' in updates && (updates.office_id || null) !== (prev?.office_id || null)) {
      const officeName = oid => oid
        ? (offices.find(o => o.id === oid)?.name || oid)
        : tLog('log_office_none')
      logEvent(id, agent.name, 'office', tLog('log_office_move', {
        by,
        from: officeName(prev?.office_id || null),
        to:   officeName(updates.office_id || null),
      }))
    }

    if (updates.resign_date || updates.resign_reason) {
      logEvent(id, agent.name, 'left',
        tLog('log_agent_resign', {
          by,
          date:   updates.resign_date   ? tLog('log_diff_date',   { date: updates.resign_date })     : '',
          reason: updates.resign_reason ? tLog('log_diff_reason',  { reason: updates.resign_reason }) : '',
        })
      )
    }

    if (updates.schedule) {
      const changes = diffSchedule(prev?.schedule, updates.schedule)
      if (changes.length > 0) {
        changes.forEach(change => {
          logEvent(id, agent.name, 'schedule', tLog('log_schedule_change', { by, change }))
        })
      }
    }

    return agent
    // `offices` joined the deps for B361's name lookup — a stale closure here would print
    // a uuid instead of an office name for any office created since this callback was made.
  }, [agents, offices])

  const removeAgent = useCallback(async (id) => {
    const agent = agents.find(a => a.id === id)
    if (agent) {
      logEvent(id, agent.name, 'left',
        tLog('log_agent_removed', { by: getCurrentAdmin(), reason: agent.resign_reason ? tLog('log_diff_reason', { reason: agent.resign_reason }) : '' })
      )
    }
    await deleteAgent(id)
    setAgents(prev => prev.filter(a => a.id !== id))
  }, [agents])

  const editOffice = useCallback(async (id, updates, prevCapacity) => {
    const office = await updateOffice(id, updates)
    setOffices(prev => prev.map(o => o.id === id ? office : o))
    const by = getCurrentAdmin()
    if (updates.capacity !== undefined && updates.capacity !== prevCapacity) {
      logEvent('system', 'System', 'office',
        tLog('log_office_capacity', { by, from: prevCapacity, to: updates.capacity }))
    }
    if (updates.name || updates.location) {
      const parts = []
      if (updates.name)     parts.push(tLog('log_office_name_part', { name: updates.name }))
      if (updates.location) parts.push(tLog('log_office_location_part', { location: updates.location }))
      logEvent('system', 'System', 'office',
        tLog('log_office_settings', { by, parts: parts.join(', ') }))
    }
    return office
  }, [])

  const createOffice = useCallback(async (data) => {
    const office = await addOffice(data)
    setOffices(prev => [...prev, office])
    const by = getCurrentAdmin()
    logEvent('system', 'System', 'office',
      tLog('log_office_settings', { by, parts: tLog('log_office_name_part', { name: office.name }) }))
    return office
  }, [])

  const removeOffice = useCallback(async (id) => {
    const office = offices.find(o => o.id === id)
    await deleteOffice(id)
    setOffices(prev => prev.filter(o => o.id !== id))
    setAgents(prev => prev.map(a => a.office_id === id ? { ...a, office_id: null } : a))
    const by = getCurrentAdmin()
    logEvent('system', 'System', 'office',
      tLog('log_office_deleted', { by, name: office?.name || id }))
  }, [offices])

  return { agents, offices, loading, error, reload, createAgent, editAgent, removeAgent, editOffice, createOffice, removeOffice }
}
